import useToken from 'hooks/useToken';
import React, { useEffect, useRef, useState, useContext } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Card, Col, Row, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileAlt } from '@fortawesome/free-solid-svg-icons';
import { useToast } from 'context/providers/ToastProvider';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

import NominaCard from './NominaCard';
import { ContabilidadLayout } from 'akasia-components/Contabilidad/common/ContabilidadLayout';
import { ContabilidadContext } from 'akasia-components/Contabilidad/common/ContabilidadContext';

const ListaNominaContent = () => {

    const { showErrorToast } = useToast();
    const { token, setToken } = useToken();
    const { selectedCompany, globalDateQuarter: dateQuarter, setGlobalDateQuarter: setDateQuarter } = useContext(ContabilidadContext);

    const [nominas, setNominas] = useState([]);
    const [cargando, setCargando] = useState(true);

    // Estado para nóminas seleccionadas
    const [selectedNominas, setSelectedNominas] = useState([]);

    // Estados para infinite scroll
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize] = useState(20);
    const [hasMoreData, setHasMoreData] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [totalRecords, setTotalRecords] = useState(0);

    // ─── Resetear y recargar desde página 1 ───────────────────────────────────
    const resetAndFetch = () => {
        setPageNumber(1);
        setHasMoreData(true);
        setNominas([]);
        setSelectedNominas([]);
        obtenerListaNomina(1, false);
    };

    const resetAndFetchConFecha = (date) => {
        setPageNumber(1);
        setHasMoreData(true);
        setNominas([]);
        setSelectedNominas([]);
        obtenerListaNomina(1, false, date);
    };

    // ─── Efectos iniciales ────────────────────────────────────────────────────
    useEffect(() => {
        if (selectedCompany && selectedCompany.vRFC) {
            resetAndFetch();
        } else {
            setNominas([]);
            setTotalRecords(0);
        }
    }, [selectedCompany?.vRFC]);

    // ─── Manejo de cambio de fecha ────────────────────────────────────────────
    const handleDateChange = (date) => {
        setDateQuarter(date);
        if (selectedCompany?.vRFC) {
            resetAndFetchConFecha(date);
        }
    };

    // ─── Manejo de selección ──────────────────────────────────────────────────
    const handleSelectNomina = (uuid) => {
        setSelectedNominas(prev =>
            prev.includes(uuid)
                ? prev.filter(id => id !== uuid)
                : [...prev, uuid]
        );
    };

    // ObtenerRFCReceptor fue eliminado a favor del contexto global

    // ─── Obtener lista de nóminas con paginación ──────────────────────────────
    const obtenerListaNomina = async (page = 1, append = false, explicitDate = null) => {
        if (!append) {
            setCargando(true);
        } else {
            setLoadingMore(true);
        }

        let date_ = explicitDate;
        if (!date_) {
            date_ = dateQuarter;
        }

        const month = new Date(date_).getMonth() + 1;
        const year = new Date(date_).getFullYear();
        const formattedMonth = month < 10 ? '0' + month : '' + month;

        const requestData = {
            year: '' + year,
            month: formattedMonth,
            rfc: selectedCompany?.vRFC || '',
            pageNumber: page,
            pageSize: pageSize
        };

        const response = await fetch('/api/contabilidad/Nomina/ObtenerListaNominas', {
            method: 'POST',
            headers: {
                'Authorization': 'Bearer ' + token,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(requestData)
        }).catch((error) => {
            showErrorToast('Hubo un problema con la peticion Fetch:' + error);
        });

        if (response && response.ok) {
            const data = await response.json();

            if (data.iResponseType == 1) {
                const ObjDetalles = JSON.parse(data.detalles);
                const nuevasNominas = ObjDetalles.table2 || [];

                if (append) {
                    setNominas(prev => [...prev, ...nuevasNominas]);
                } else {
                    setNominas(nuevasNominas);
                    setPageNumber(1);
                }

                const total = ObjDetalles.table3?.[0]?.totalRecords
                    ?? (append ? nominas.length + nuevasNominas.length : nuevasNominas.length);
                setTotalRecords(total);

                if (nuevasNominas.length < pageSize) {
                    setHasMoreData(false);
                } else {
                    setHasMoreData((page * pageSize) < total);
                }
            } else {
                showErrorToast(data.vMessage);
                if (!append) {
                    setNominas([]);
                    setHasMoreData(false);
                }
            }
        } else if (response) {
            showErrorToast(response.status + ' ' + response.statusText);
            if (response.status == 401) {
                setToken(null);
                location.href = '';
            }
            if (!append) {
                setNominas([]);
                setHasMoreData(false);
            }
        }

        if (!append) {
            setCargando(false);
        } else {
            setLoadingMore(false);
        }
    };

    // ─── Cargar más (infinite scroll) ─────────────────────────────────────────
    const cargarMasDatos = () => {
        if (hasMoreData && !loadingMore && !cargando) {
            const nextPage = pageNumber + 1;
            setPageNumber(nextPage);
            obtenerListaNomina(nextPage, true);
        }
    };

    // ─── Trigger de Intersection Observer ────────────────────────────────────
    const InfiniteScrollTrigger = () => {
        const triggerRef = useRef();

        useEffect(() => {
            const observer = new IntersectionObserver(
                (entries) => {
                    if (entries[0].isIntersecting && hasMoreData && !loadingMore && !cargando) {
                        cargarMasDatos();
                    }
                },
                { threshold: 0.1 }
            );

            if (triggerRef.current) {
                observer.observe(triggerRef.current);
            }

            return () => {
                if (triggerRef.current) {
                    observer.unobserve(triggerRef.current);
                }
            };
        }, [hasMoreData, loadingMore, cargando]);

        if (!hasMoreData) return null;

        return (
            <div ref={triggerRef} className="text-center py-4">
                {loadingMore ? (
                    <div>
                        <Spinner animation="border" variant="primary" className="mb-2" />
                        <div className="text-muted small">Cargando más nóminas...</div>
                    </div>
                ) : (
                    <div className="text-muted small">Desplázate para cargar más</div>
                )}
            </div>
        );
    };

    // ─── Skeleton de carga ────────────────────────────────────────────────────
    const SkeletonCards = () => (
        <div className="p-4">
            {[...Array(5)].map((_, index) => (
                <Card key={index} className="mb-3">
                    <Card.Body>
                        <Row>
                            <Col xs={12} md={3}>
                                <Skeleton height={28} width={120} className="mb-2" />
                                <Skeleton height={14} width={80} className="mb-1" />
                                <Skeleton height={14} width={100} />
                            </Col>
                            <Col xs={12} md={6}>
                                <Skeleton height={18} className="mb-2" />
                                <Skeleton height={14} width={160} className="mb-1" />
                                <Skeleton height={14} width={120} />
                            </Col>
                            <Col xs={12} md={3} className="text-end">
                                <Skeleton height={26} width={110} className="mb-2" />
                                <Skeleton height={14} width={80} className="mb-1" />
                                <Skeleton height={14} width={90} />
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>
            ))}
        </div>
    );

    // ─── Render ───────────────────────────────────────────────────────────────
    return (
        <Row>
            <Col>
                <Card className="mb-3">
                    <Card.Header className="pb-0 bg-light">
                        <Row className="align-items-center mb-3">
                            <Col xs={12} className="text-md-end mt-3 mt-md-0">
                                <div className="d-inline-block text-start">
                                    <DatePicker
                                        selected={dateQuarter}
                                        onChange={handleDateChange}
                                        dateFormat="MMMM yyyy"
                                        className="form-control form-control-sm"
                                        showMonthYearPicker
                                    />
                                </div>
                            </Col>
                        </Row>
                    </Card.Header>

                    <Card.Body className="p-0">
                        {cargando ? (
                            <SkeletonCards />
                        ) : nominas.length > 0 ? (
                            <div className="p-3">
                                {/* Contador */}
                                <div className="mb-3 d-flex justify-content-between align-items-center">
                                    <small className="text-muted">
                                        Mostrando {nominas.length} de {totalRecords} nóminas
                                    </small>
                                </div>

                                {/* Cards */}
                                {nominas.map((nomina, index) => (
                                    <NominaCard
                                        key={`${nomina.vFolio}-${nomina.NumEmpleado}-${index}`}
                                        nomina={nomina}
                                        isSelected={selectedNominas.includes(nomina.vUUIDFactura)}
                                        onSelect={() => handleSelectNomina(nomina.vUUIDFactura)}
                                        rfcEmisor={selectedCompany?.vRFC}
                                    />
                                ))}

                                <InfiniteScrollTrigger />
                            </div>
                        ) : (
                            <div className="text-center py-5">
                                <div className="mb-3">
                                    <FontAwesomeIcon
                                        icon={faFileAlt}
                                        size="3x"
                                        className="text-muted"
                                    />
                                </div>
                                <h5 className="text-muted">No se encontraron nóminas</h5>
                                <p className="text-muted">
                                    No hay nóminas registradas para el periodo y RFC seleccionados
                                </p>
                            </div>
                        )}
                    </Card.Body>
                </Card>
            </Col>
        </Row>
    );
};

const ListaNomina = () => {
    return (
        <ContabilidadLayout
            title={
                <><FontAwesomeIcon icon={faFileAlt} className="me-2" />Catálogo de Nómina</>
            }
        >
            <ListaNominaContent />
        </ContabilidadLayout>
    );
};

export default ListaNomina;
